/*
 * Mobile-only video modal for the "Custom Video (Modal)" section.
 *
 * Desktop keeps the theme's default behaviour: tapping the poster loads the
 * YouTube/Vimeo iframe in place via the existing <deferred-media> element.
 *
 * On mobile (< breakpoint) we intercept the poster click in the CAPTURE phase
 * on this wrapper element. Because capture listeners on an ancestor always run
 * before listeners on the target, calling stopImmediatePropagation() here
 * reliably prevents DeferredMedia's in-place loader (bound to the poster
 * button) from firing, and we open a modal instead. The iframe markup is cloned
 * from the same <template> the deferred-media uses, so there is one source of
 * truth for the embed.
 *
 * The modal element is relocated to <body> on connect (like the theme's own
 * ModalDialog) so it escapes the section's stacking context and renders above
 * the sticky header, back-to-top button and chat widget.
 */
if (!customElements.get('mobile-video-modal')) {
  customElements.define(
    'mobile-video-modal',
    class MobileVideoModal extends HTMLElement {
      constructor() {
        super();

        this.breakpoint = parseInt(this.dataset.breakpoint || '750', 10);
        this.deferred = this.querySelector('deferred-media');
        this.modal = this.querySelector('.video-modal');
        this.videoTarget = this.modal ? this.modal.querySelector('[data-modal-video-target]') : null;
        this.closeButton = this.modal ? this.modal.querySelector('[data-modal-close]') : null;
        this.template = this.deferred ? this.deferred.querySelector('template') : null;

        if (!this.deferred || !this.modal || !this.videoTarget || !this.closeButton || !this.template) {
          return;
        }

        this.mql = window.matchMedia(`(max-width: ${this.breakpoint - 1}px)`);
        this.scrollY = 0;
        this.onKeyup = this.onKeyup.bind(this);

        // Capture phase on the wrapper: runs before the poster button's own
        // (bubble-phase) DeferredMedia click handler, so we can suppress it.
        this.addEventListener('click', this.onCaptureClick.bind(this), true);

        this.closeButton.addEventListener('click', this.close.bind(this));
        this.modal.addEventListener('click', (event) => {
          if (event.target === this.modal) this.close();
        });
      }

      connectedCallback() {
        // Move the modal out of the section so a fixed-position overlay isn't
        // trapped inside an ancestor stacking context. Guard against the theme
        // editor re-rendering the section and leaving a stale copy in <body>.
        if (this.modal && this.modal.parentElement !== document.body) {
          const existing = document.getElementById(this.modal.id);
          if (existing && existing !== this.modal) existing.remove();
          document.body.appendChild(this.modal);
        }
      }

      onCaptureClick(event) {
        const poster = event.target.closest('button.video-section__poster');
        if (!poster) return;

        // Desktop: let the click bubble to DeferredMedia for in-place playback.
        if (!this.mql.matches) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this.open();
      }

      open() {
        if (!this.videoTarget.hasChildNodes()) {
          this.videoTarget.appendChild(this.template.content.cloneNode(true));
        }

        if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();

        // iOS-safe scroll lock: overflow:hidden alone doesn't stop Safari, so we
        // pin the body in place and restore the scroll position on close.
        this.scrollY = window.scrollY || window.pageYOffset || 0;
        document.body.style.top = `-${this.scrollY}px`;
        document.body.classList.add('video-modal-open');

        this.modal.setAttribute('open', '');
        document.addEventListener('keyup', this.onKeyup);

        if (typeof trapFocus === 'function') trapFocus(this.modal, this.closeButton);
      }

      close() {
        this.modal.removeAttribute('open');
        document.body.classList.remove('video-modal-open');
        document.body.style.top = '';
        window.scrollTo(0, this.scrollY);
        document.removeEventListener('keyup', this.onKeyup);

        // Remove the iframe so the video actually stops playing.
        this.videoTarget.replaceChildren();

        if (typeof removeTrapFocus === 'function') {
          removeTrapFocus(this.querySelector('button.video-section__poster'));
        }
      }

      onKeyup(event) {
        if (event.code && event.code.toUpperCase() === 'ESCAPE') this.close();
      }
    }
  );
}
